// =====================================================================
// commande-prete/chargement.js — Commandes prêtes v2 (16-10-2026) — CHARGEMENT
// =====================================================================
// Écran « Chargement » (CommandesPretesChargement.jsx, SPEC-v2 §3 et §5) : un
// départ de camion (LoadingDTO) = les commandes NON test du client qui ont
// encore une unité à quai, ou une unité chargée dans CE départ.
//   LoadingDTO = { id, client, truck, createdAt, createdByName, closedAt, mailState, mailError,
//                  commandes: [{ id, orderRef, isTest, statutMail (ENVOYE | A_VERIFIER | EN_COURS | JAMAIS),
//                                unites: [{ id, nom, kind, sscc, loadedAt, loadingId }] }] }
//   Réponses : POST /loadings → LoadingDTO + existing ; scan → + scan { orderRef, nom, sscc },
//   avertissements ; load-order → + charge, avertissements ; undo → + annule [{ orderRef, nom }] ;
//   close → + mail. Erreurs : { error, message, dto: LoadingDTO }.
// Une unité est « à quai » (pas de loadedAt), « chargée » dans CE départ
// (loadingId = id du départ) ou chargée dans un départ PRÉCÉDENT.
// Le serveur fait foi (scan, « Tout charger », annulation, fermeture) ; ces
// fonctions ne font que lire l'état pour l'écran.
// v3 (décision Silviu 16/10) : plus de client au départ — le départ liste les commandes OUVERTES (réf.
// de chargement scannée, ou unité chargée), chacune avec son `client` ; scan = SSCC (→ `scan`) ou réf.
// (→ `ouvert` { orderId, orderRef, client }, ou `choix` [{ orderId, orderRef, client, isTest,
// chargeable, motif }] si la réf. existe chez plusieurs clients / en test et en réel) ; validation →
// `mails` [{ client, state, error }] : un mail « chargée » par client.
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

import { normSscc, motifSsccInvalide } from './sscc.js';
import { verifRefChargement } from './regles.js';

export const MSG_SCAN_VIDE = 'Scannez la réf. de chargement (BROUT…) ou l\'étiquette de la palette / du colis';
export const MSG_AUCUNE_COMMANDE = 'Aucune commande dans ce départ : scannez une réf. de chargement (BROUT…) pour ouvrir sa commande, ou directement l\'étiquette d\'une palette / d\'un colis.';
export const MSG_RIEN_CHARGE = 'Rien n\'est chargé dans ce départ : scannez au moins une palette ou un colis.';
export const MSG_DEPART_FERME = 'Départ déjà validé : il ne se modifie plus.';

// Réponse acceptée = le LoadingDTO (+ champs) ; réponse d'erreur = { error, message, dto }.
export function extraireChargement(r) {
  if (r && Array.isArray(r.commandes)) return r;
  if (r && r.dto && Array.isArray(r.dto.commandes)) return r.dto;
  return null;
}
// Avertissements joints à une réponse acceptée (ex. « Commande X : mail jamais envoyé »).
export const avertissementsReponse = (r) => (r && Array.isArray(r.avertissements) ? r.avertissements.filter((x) => typeof x === 'string' && x) : []);
// « Palette 2 · BROUT117200 » : l'unité que le serveur vient de charger (réponse du scan).
// (v3 : départ de plusieurs clients — la commande est nommée avec son client, pris dans le départ renvoyé)
const clientDeLaCommande = (r, orderId) => {
  const c = r && Array.isArray(r.commandes) ? r.commandes.find((x) => x.id === orderId) : null;
  return c && c.client && c.client.name ? ` (${c.client.name})` : '';
};
export const texteScan = (r) => (r && r.scan && r.scan.nom ? `${r.scan.nom} · ${r.scan.orderRef}${clientDeLaCommande(r, r.scan.orderId)}` : null);
// v3 : réf. scannée → commande ouverte dans le départ (réponse du serveur).
export const texteOuvert = (r) => (r && r.ouvert && r.ouvert.orderRef
  ? `${r.ouvert.orderRef}${r.ouvert.client && r.ouvert.client.name ? ` (${r.ouvert.client.name})` : ''} ouverte : scannez ses palettes / colis, ou « Tout charger ».`
  : null);
// v3 : réf. ambiguë → les commandes proposées (la commande de test : montrée, jamais choisissable).
export function lireChoix(r) {
  if (!r || !Array.isArray(r.choix)) return [];
  return r.choix.filter((c) => c && c.orderId).map((c) => ({
    orderId: c.orderId, orderRef: c.orderRef, client: c.client || null, isTest: !!c.isTest,
    chargeable: c.chargeable === true && !c.isTest, motif: c.motif || (c.isTest ? 'Commande de test : elle ne se charge pas' : null),
  }));
}
export const libelleChoix = (c) => `${c.orderRef} — ${(c.client && c.client.name) || 'client inconnu'}${c.isTest ? ' (TEST)' : ''}`;
// Nom du client d'une commande du départ (v3 : chaque commande porte le sien).
export const clientDe = (c) => (c && c.client && c.client.name) || '—';

// Unités remises à quai par « Annuler le dernier scan » (réponse du serveur).
export const texteAnnule = (r) => (r && Array.isArray(r.annule) && r.annule.length ? r.annule.map((x) => `${x.nom} · ${x.orderRef}${clientDeLaCommande(r, x.orderId)}`).join(', ') : null);

// 'QUAI' (pas chargée) | 'ICI' (chargée dans ce départ) | 'AVANT' (chargée dans un autre départ).
export function etatUnite(u, lid) {
  if (!u || !u.loadedAt) return 'QUAI';
  return u.loadingId === lid ? 'ICI' : 'AVANT';
}

// quai = unités À QUAI avec une étiquette (chargeables : scan ou « Tout charger ») ;
// sansEtiquette = fiches à quai SANS SSCC — le serveur ne les charge pas (elles sont à compléter sur
// l'écran de la commande) ; elles restent physiquement à quai : comptées dans ce qui reste.
export function resumeCommande(c, lid) {
  const unites = (c && c.unites) || [];
  let quai = 0, quaiPal = 0, sansEtiquette = 0, ici = 0, avant = 0;
  const idsQuai = [];   // à quai AVEC étiquette : ce que « Tout charger » charge
  for (const u of unites) {
    const e = etatUnite(u, lid);
    if (e === 'QUAI') {
      if (u.sscc) { quai += 1; idsQuai.push(u.id); if (u.kind === 'PAL') quaiPal += 1; } else sansEtiquette += 1;
    } else if (e === 'ICI') ici += 1; else avant += 1;
  }
  const total = unites.length;
  const reste = quai + sansEtiquette;
  // Aucune unité : rien à charger — ni « à quai », ni « chargée ».
  const etat = !total ? 'VIDE' : reste === total ? 'QUAI' : reste === 0 ? 'CHARGEE' : 'PARTIELLE';
  return { total, quai, quaiPal, idsQuai, sansEtiquette, ici, avant, etat };
}

// Bilan du départ : ce qui monte dans CE camion, et ce qui reste à quai pour les commandes concernées.
export function resumeDepart(ch) {
  const lid = ch && ch.id;
  const charge = [], reste = [];
  let unitesIci = 0, totalQuai = 0;
  for (const c of (ch && ch.commandes) || []) {
    const r = resumeCommande(c, lid);
    // (v3 : départ de plusieurs clients — la même réf. peut exister chez deux clients : nommée avec son client)
    const nom = c.client && c.client.name ? `${c.orderRef} (${c.client.name})` : c.orderRef;
    if (r.ici) { charge.push({ orderRef: c.orderRef, nom, n: r.ici }); unitesIci += r.ici; }
    const n = r.quai + r.sansEtiquette;   // ce qui reste physiquement à quai (fiches sans étiquette comprises)
    if (n) { reste.push({ orderRef: c.orderRef, nom, n, concernee: r.ici > 0 }); totalQuai += n; }
  }
  return { unitesIci, commandesIci: charge.length, charge, reste, totalQuai };
}

const temps = (d) => { const t = new Date(d).getTime(); return Number.isFinite(t) ? t : 0; };

// Ce que « Annuler le dernier scan » remettra à quai — comme le serveur : TOUTES les unités de ce
// départ chargées au DERNIER instant (un scan = 1 unité ; « Tout charger » = toute la commande).
// { at, unites: [{ commande, unite }] } ou null.
export function dernierLot(ch) {
  if (!ch) return null;
  let at = null;
  const ici = [];
  for (const c of ch.commandes || []) {
    for (const u of c.unites || []) {
      if (etatUnite(u, ch.id) !== 'ICI') continue;
      ici.push({ commande: c, unite: u });
      if (at === null || temps(u.loadedAt) > at) at = temps(u.loadedAt);
    }
  }
  if (!ici.length) return null;
  return { at, unites: ici.filter((x) => temps(x.unite.loadedAt) === at) };
}
// « Palette 2 · BROUT1 » ; un lot : « 12 unités de BROUT1 » (ou de plusieurs commandes).
export function texteLot(lot) {
  if (!lot || !lot.unites.length) return null;
  const nomCmd = (c) => (c.client && c.client.name ? `${c.orderRef} (${c.client.name})` : c.orderRef);
  if (lot.unites.length === 1) return `${lot.unites[0].unite.nom} · ${nomCmd(lot.unites[0].commande)}`;
  const refs = [...new Set(lot.unites.map((x) => nomCmd(x.commande)))];
  return `${texteUnites(lot.unites.length)} de ${refs.join(', ')}`;
}

export const departFerme = (ch) => !!(ch && ch.closedAt);
export const peutAnnuler = (ch) => !!ch && !departFerme(ch) && !!dernierLot(ch);

// « Valider le départ » : jamais un départ vide ni un départ déjà fermé (message humain).
export function peutValider(ch) {
  if (!ch) return { ok: false, msg: '' };
  if (departFerme(ch)) return { ok: false, msg: MSG_DEPART_FERME };
  if (!resumeDepart(ch).unitesIci) return { ok: false, msg: MSG_RIEN_CHARGE };
  return { ok: true, msg: '' };
}

// « Tout charger » d'une commande : seulement s'il reste quelque chose à quai, départ ouvert,
// et jamais une commande de test (elles ne partent pas chez le client).
export function peutToutCharger(ch, c) {
  if (!ch || departFerme(ch) || !c || c.isTest) return false;
  return resumeCommande(c, ch.id).quai > 0;
}

const pl = (n, s, p) => `${n} ${n > 1 ? p : s}`;
export const texteUnites = (n) => pl(n, 'unité', 'unités');

/**
 * Ce que « Tout charger » charge (les unités À QUAI qui ont une étiquette, `quai` de resumeCommande), en palettes /
 * colis : « 5 palettes », « 1 palette + 2 colis » (29-10-2026, demande Silviu : « qu'il dise combien de palettes il y a
 * à sortir, ex. 5 palettes, avec le bouton pour les sortir toutes »).
 */
export function texteAQuai(rc) {
  const col = rc.quai - rc.quaiPal;
  return [rc.quaiPal ? pl(rc.quaiPal, 'palette', 'palettes') : '', col ? pl(col, 'colis', 'colis') : ''].filter(Boolean).join(' + ') || texteUnites(0);
}
/**
 * « Tout charger » touché : ce que l'opérateur va confirmer, FIGÉ à ce moment — les unités à quai avec étiquette (envoyées
 * telles quelles : le serveur ne charge qu'elles, et refuse si une autre est à quai) et leur texte.
 */
export function confirmationTout(c, lid) {
  const rc = resumeCommande(c, lid);
  return { lid, orderId: c.id, orderRef: c.orderRef, ids: rc.idsQuai, texte: texteAQuai(rc) };
}
const commandeDe = (a, ch) => (a && ch && a.lid === ch.id ? (ch.commandes || []).find((x) => x.id === a.orderId) : null);
/** La confirmation en attente vaut encore : MÊME départ, la commande se charge toujours, avec les MÊMES unités à quai. */
export function confirmationValable(a, ch) {
  const c = commandeDe(a, ch);
  if (!c || !peutToutCharger(ch, c)) return false;
  const ids = resumeCommande(c, ch.id).idsQuai;
  const montrees = new Set(a.ids);
  return ids.length === montrees.size && ids.every((id) => montrees.has(id));
}
/**
 * Confirmation devenue fausse : refaite avec les unités à quai de MAINTENANT et signalée (`change`) — ou null si la
 * commande ne se charge plus dans CE départ (tout chargé, départ validé, autre départ : l'écran le montre déjà).
 */
export function confirmationRefaite(a, ch) {
  const c = commandeDe(a, ch);
  return c && peutToutCharger(ch, c) ? { ...confirmationTout(c, ch.id), change: true } : null;
}
/**
 * Après « Oui » : ce que CE geste a chargé (`charge.ids` de la réponse), en palettes / colis comme la phrase confirmée ;
 * les autres unités confirmées étaient déjà dans ce camion.
 */
export function texteToutCharge(c, conf, charge) {
  const ids = new Set((charge && charge.ids) || []);
  if (!ids.size) return `✓ ${c.orderRef} : tout était déjà dans ce camion`;
  const pal = ((c && c.unites) || []).filter((u) => ids.has(u.id) && u.kind === 'PAL').length;
  const deja = conf.ids.length - ids.size;
  return `✓ ${c.orderRef} : ${texteAQuai({ quai: ids.size, quaiPal: pal })} dans le camion, sans scan, à votre nom${deja > 0 ? ` — ${deja} y étai${deja > 1 ? 'ent' : 't'} déjà` : ''}`;
}
/** Phrase que l'opérateur confirme : chargé SANS scan, à son nom (« s'il oublie une palette, il l'assume »). */
export const texteConfirmerTout = (c, conf, nom) =>
  `Je confirme avoir chargé ${conf.texte} de la commande ${c.orderRef} dans le camion, sans scan${nom ? ` — à mon nom (${nom})` : ''}.`;

// Récapitulatif affiché AVANT la confirmation du départ (sur la page, jamais window.confirm).
export function texteRecapDepart(ch) {
  const r = resumeDepart(ch);
  const lignes = [];
  lignes.push(`Chargé dans ce départ : ${texteUnites(r.unitesIci)} (${pl(r.commandesIci, 'commande', 'commandes')}).`);
  for (const x of r.charge) lignes.push(`  ${x.nom} : ${texteUnites(x.n)} chargée${x.n > 1 ? 's' : ''}`);
  const concernees = r.reste.filter((x) => x.concernee);
  if (concernees.length) {
    lignes.push(`Encore à quai pour ces commandes : ${concernees.map((x) => `${x.nom} (${texteUnites(x.n)})`).join(', ')}.`);
  } else lignes.push('Rien ne reste à quai pour les commandes chargées.');
  const autres = r.reste.filter((x) => !x.concernee);
  if (autres.length) lignes.push(`Non chargées dans ce départ : ${autres.map((x) => x.nom).join(', ')}.`);
  return lignes;
}

// Code scanné → forme normalisée (comme le SSCC des fiches) ; vide → message.
export function verifCodeScan(raw) {
  const code = normSscc(raw);
  if (!code) return { erreur: MSG_SCAN_VIDE };
  return { code };
}

/**
 * Réf. de commande tapée / scannée dans « Camion » (29-10-2026 : Vincent y scanne la réf. BROUT… ; Silviu : « scanner
 * seulement la commande ») → le code à ouvrir aussi dans le départ, comme scanné (même lecture que le scan). Seulement
 * la forme des réf. vues en prod, BROUT + chiffres (valable à l'encodage) : une plaque, « DHL », « Broutin Transports »…
 * restent le nom du camion, sans requête. Et JAMAIS une étiquette de palette (envoyée au scan, elle serait CHARGÉE) :
 * motifSsccInvalide est la règle même du serveur pour chercher une unité — la garantie tient si les formats changent.
 * → le code normalisé, ou null.
 */
const REF_BROUT = /^BROUT\d+$/;
export function refDuCamion(truck) {
  const { code } = verifCodeScan(truck);
  return code && REF_BROUT.test(code) && !verifRefChargement(code) && motifSsccInvalide(code) ? code : null;
}

// Départ avec des unités chargées (dans CE départ) un AUTRE jour, à l'heure de Bruxelles — même jour que le serveur
// (ready-loading.service.js, jour()), qui refuse alors d'y joindre un camion : rien d'aujourd'hui ne doit y monter.
const jourBxl = (d) => new Date(d).toLocaleDateString('fr-BE', { timeZone: 'Europe/Brussels' });
export function chargeAutreJour(dto, maintenant = new Date()) {
  if (!dto) return false;
  const auj = jourBxl(maintenant);
  return (dto.commandes || []).some((c) => (c.unites || []).some((u) => u.loadedAt && u.loadingId === dto.id && jourBxl(u.loadedAt) !== auj));
}

// Unité du départ portant ce code (forme normalisée), pour nommer ce qui vient d'être chargé.
export function trouverUnite(ch, code) {
  return trouverUnites(ch, code)[0] || null;
}
// Toutes les unités portant ce code (le même SSCC peut exister dans deux commandes du client).
export function trouverUnites(ch, code) {
  const n = normSscc(code);
  const res = [];
  if (!ch || !n) return res;
  for (const c of ch.commandes || []) {
    for (const u of c.unites || []) if (u.sscc && normSscc(u.sscc) === n) res.push({ commande: c, unite: u });
  }
  return res;
}
// Scan resté sans réponse : le départ relu dit ce qui s'est passé, en comparant AVANT / APRÈS.
//   { charge: { commande, unite } } — une unité de ce code est passée « chargée ici » par ce scan ;
//   { deja: { commande, unite } }   — elle l'était déjà avant (scan en double : rien de nouveau) ;
//   { ouvert: commande }             — v3 : réf. de chargement, sa commande est apparue dans le départ ;
//   {}                               — rien de chargé : à rescanner.
export function issueScanSansReponse(avant, apres, code) {
  if (!apres) return {};
  const lid = apres.id;
  // Réf. lue comme le serveur : majuscules, sans préfixe du lecteur (« ]C1 »).
  const ref = normSscc(code);
  const cmds = !trouverUnites(apres, code).length && ref ? (apres.commandes || []).filter((c) => c.orderRef === ref) : [];
  if (cmds.length) {
    // Seule une commande APPARUE dit ce que le serveur a fait ; déjà là avant, on ne sait pas (la réf. existe
    // peut-être chez un autre client : le serveur a pu proposer un choix) — rescanner, sans risque.
    const avantIds = new Set(((avant && avant.commandes) || []).map((c) => c.id));
    const nouvelles = cmds.filter((c) => !avantIds.has(c.id));
    return nouvelles.length === 1 ? { ouvert: nouvelles[0] } : {};
  }
  const avantIci = new Set(trouverUnites(avant, code).filter((x) => etatUnite(x.unite, lid) === 'ICI').map((x) => x.unite.id));
  const ici = trouverUnites(apres, code).filter((x) => etatUnite(x.unite, lid) === 'ICI');
  const nouvelle = ici.find((x) => !avantIci.has(x.unite.id));
  if (nouvelle) return { charge: nouvelle };
  if (ici.length) return { deja: ici[0] };
  return {};
}

// État du mail « commande prête » d'une commande (statutMail : ENVOYE | A_VERIFIER | EN_COURS | JAMAIS).
export function etatMailCommande(c) {
  switch (c && c.statutMail) {
    case 'ENVOYE': return { ok: true, txt: 'mail envoyé' };
    case 'A_VERIFIER': return { ok: false, txt: 'envoi du mail à vérifier' };
    case 'EN_COURS': return { ok: false, txt: 'envoi du mail en cours' };
    default: return { ok: false, txt: 'mail jamais envoyé' };
  }
}

// Mail « chargée » après la validation du départ (mailState du départ : jamais un état passé sous silence).
export function texteMailDepart(ch) {
  if (!ch || !ch.closedAt) return null;
  const err = ch.mailError ? ` : ${ch.mailError}` : '';
  switch (ch.mailState) {
    case 'ENVOYE': return { ok: true, txt: 'Mail « chargée » envoyé au client.' };
    case 'ECHEC': return { ok: false, txt: `Mail « chargée » NON parti${err}.` };
    case 'A_VERIFIER': return { ok: false, txt: `Mail « chargée » à vérifier — il est peut-être parti : regardez les Éléments envoyés de zeus@ avant de prévenir le client${err}.` };
    case 'EN_COURS': return { ok: false, txt: 'Mail « chargée » en cours d\'envoi : rouvrez ce départ dans un instant pour voir s\'il est parti.' };
    // (avec un motif : le client attendait un mail, il n'est pas parti — client inactif ou plus proposé)
    case 'NON_CONFIGURE': return ch.mailError ? { ok: false, txt: `Pas de mail « chargée »${err}.` } : { ok: true, txt: 'Pas de mail au chargement pour ce client (Paramètres clients).' };
    default: return { ok: false, txt: 'État du mail « chargée » inconnu.' };
  }
}
// Un mail « chargée » encore incertain (en cours, à vérifier) — chez UN des clients du départ suffit
// (le résumé du départ peut dire ECHEC pour un autre client) : l'écran propose de recharger l'état.
export function mailIncertain(ch) {
  if (!ch || !ch.closedAt) return false;
  const incertain = (s) => s === 'EN_COURS' || s === 'A_VERIFIER';
  return incertain(ch.mailState) || (Array.isArray(ch.mails) && ch.mails.some((m) => m && incertain(m.state)));
}
// Départ « pour un client » ouvert par l'ancien écran (avant la v3), repris par le nouvel écran.
export const texteDepartReserve = (ch) => (ch && ch.client && ch.client.name && !ch.closedAt
  ? `Départ réservé à ${ch.client.name} (ouvert avant la mise à jour) : seules ses commandes s'y chargent — validez-le, puis commencez un nouveau départ pour les autres clients.`
  : null);

// v3 : un mail « chargée » PAR CLIENT — une ligne par client (un seul client : la phrase d'avant).
export function textesMailsDepart(ch) {
  if (!ch || !ch.closedAt) return [];
  const mails = Array.isArray(ch.mails) ? ch.mails : [];
  if (mails.length <= 1) { const t = texteMailDepart(ch); return t ? [t] : []; }
  return mails.map((m) => {
    const t = texteMailDepart({ closedAt: ch.closedAt, mailState: m.state, mailError: m.error });
    return { ok: t.ok, txt: `${(m.client && m.client.name) || '—'} : ${t.txt}` };
  });
}
